/**
 * Os chamados de um cliente, sem depender do que a tela já tinha carregado.
 *
 * ## Por que a página do cliente precisava disto
 *
 * Ela filtrava a lista GLOBAL que a tela de chamados carrega -- e essa lista é
 * "os ativos mais os 100 finalizados mais recentes". Para um cliente
 * específico, os finalizados dele quase nunca estão ali.
 *
 * O efeito, relatado pela Gisele em 18/10/2026: na aba do cliente, incluir
 * cancelados e resolvidos "não gera" nada. Não era o filtro: era a lista, que
 * nunca teve aqueles chamados.
 *
 * É o mesmo modo de falha da Busca Avançada, documentado em
 * `utils/condicoesDaBuscaAvancada`: refinar no navegador só alcança o que já
 * foi baixado, e quem olha não tem como saber a diferença.
 */

/**
 * Junta duas listas de chamados sem repetir.
 *
 * A ordem importa: o que vem da LISTA JÁ CARREGADA ganha, porque ela é
 * atualizada ao vivo pela tela (mudança de status, de responsável). O que vem
 * do servidor entra só para completar o que faltava.
 *
 * @param {Array} jaCarregados
 * @param {Array} doServidor
 */
export const mesclarChamadosSemRepetir = (jaCarregados, doServidor) => {
    const lista = Array.isArray(jaCarregados) ? [...jaCarregados] : [];
    const vistos = new Set(lista.map((t) => String(t?.id)));

    (Array.isArray(doServidor) ? doServidor : []).forEach((t) => {
        const id = String(t?.id);
        if (id === 'undefined' || vistos.has(id)) return;

        vistos.add(id);
        lista.push(t);
    });

    return lista;
};

/**
 * Os pedidos a fazer ao servidor -- um por EIXO, e não um só com os dois.
 *
 * A tela considera do cliente o chamado ligado a ele **ou** à empresa dele
 * (`ticketBelongsToClient`). O servidor, porém, combina `client_id` e
 * `company_id` com **E**: mandar os dois juntos devolveria só a interseção,
 * deixando de fora tanto o chamado do cliente sem empresa quanto o da empresa
 * aberto por outro contato.
 *
 * Então são dois pedidos, e a união deles reproduz o OU. O recorte final
 * continua sendo o da própria tela, que é quem define "deste cliente".
 *
 * Devolve `[]` quando não há cliente: pedir sem recorte traria a base inteira,
 * que é o oposto do que esta tela quer.
 *
 * @returns {Array<object>} um conjunto de parâmetros por pedido
 */
export const pedidosDosChamadosDoCliente = (clientId, companyId, porPagina = 200) => {
    const comum = { per_page: porPagina, sort_by: 'created_at', sort_dir: 'desc' };
    const cid = Number(clientId);

    if (!cid || Number.isNaN(cid)) return [];

    const pedidos = [{ ...comum, client_id: cid }];
    const empresa = Number(companyId);

    if (empresa && !Number.isNaN(empresa)) pedidos.push({ ...comum, company_id: empresa });

    return pedidos;
};
